import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import jsPDF from "jspdf";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Truck, History, Plus, Trash2, FileDown, Pencil } from "lucide-react";

const eur = (n) => `€ ${Number(n || 0).toFixed(2)}`;
const WALLETS = [{ v: "contante", l: "Contanti" }, { v: "sumup", l: "SumUp" }, { v: "banca", l: "Banca" }, { v: "deposito", l: "Deposito" }];
const methodLabel = (m) => (WALLETS.find((w) => w.v === m)?.l) || m || "—";

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", vat: "" });
  const [payOpen, setPayOpen] = useState(false);
  const [paySup, setPaySup] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", payment_method: "contante" });

  const openPay = (s) => { setPaySup(s); setPayForm({ amount: String(s.debt || 0), payment_method: "contante" }); setPayOpen(true); };
  const doPay = async () => {
    const amt = parseFloat(payForm.amount);
    if (!amt || amt <= 0) return toast.error("Importo non valido");
    try { await api.post(`/suppliers/${paySup.id}/pay`, { amount: amt, payment_method: payForm.payment_method }); toast.success("Pagamento registrato"); setPayOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const load = () => api.get("/suppliers").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Inserisci il nome del fornitore");
    try {
      if (editId) await api.put(`/suppliers/${editId}`, form);
      else await api.post("/suppliers", form);
      toast.success("Fornitore salvato"); setNewOpen(false); setEditId(null); setForm({ name: "", phone: "", email: "", address: "", vat: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openEdit = (s) => { setEditId(s.id); setForm({ name: s.name || "", phone: s.phone || "", email: s.email || "", address: s.address || "", vat: s.vat || "" }); setNewOpen(true); };
  const remove = async (id) => {
    if (!window.confirm("Eliminare questo fornitore?")) return;
    try { await api.delete(`/suppliers/${id}`); toast.success("Fornitore eliminato"); load(); }
    catch { toast.error("Impossibile eliminare"); }
  };

  const generatePDF = async () => {
    let biz = { name: "Dogana 2.0", address: "", vat: "", phone: "" };
    try { biz = (await api.get("/settings/business")).data; } catch { /* noop */ }
    const logo = await new Promise((res) => {
      fetch("/logo-dogana.png").then((r) => r.blob()).then((b) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = () => res(null); fr.readAsDataURL(b); }).catch(() => res(null));
    });
    const doc = new jsPDF();
    if (logo) { try { doc.addImage(logo, "PNG", 12, 9, 20, 20); } catch { /* noop */ } }
    const hx = logo ? 37 : 12;
    doc.setFontSize(14); doc.setFont(undefined, "bold"); doc.text(biz.name || "Dogana 2.0", hx, 16);
    doc.setFontSize(9); doc.setFont(undefined, "normal"); let hy = 21;
    if (biz.address) { doc.text(biz.address, hx, hy); hy += 4.5; }
    const l2 = [biz.vat && `P.IVA: ${biz.vat}`, biz.phone && `Tel: ${biz.phone}`].filter(Boolean).join("    ");
    if (l2) doc.text(l2, hx, hy);
    doc.setDrawColor(180); doc.line(12, 32, 198, 32);
    doc.setFontSize(13); doc.setFont(undefined, "bold"); doc.text(`Elenco Fornitori — ${new Date().toLocaleDateString("it-IT")}`, 12, 40);
    let y = 50;
    doc.setFontSize(9); doc.setFont(undefined, "bold");
    doc.text("Fornitore", 12, y); doc.text("Ordini", 118, y); doc.text("Totale", 143, y); doc.text("Ultima consegna", 168, y);
    y += 2; doc.setDrawColor(220); doc.line(12, y, 198, y); y += 5;
    doc.setFont(undefined, "normal");
    items.forEach((s) => {
      if (y > 285) { doc.addPage(); y = 15; }
      doc.text(String(s.name || "").slice(0, 58), 12, y);
      doc.text(String(s.orders || 0), 118, y);
      doc.text(`EUR ${Number(s.total_spent || 0).toFixed(2)}`, 143, y);
      doc.text(String(s.last_date || "—"), 168, y);
      y += 6;
    });
    if (items.length === 0) doc.text("Nessun fornitore registrato.", 12, y);
    doc.save("elenco-fornitori.pdf");
    toast.success("PDF fornitori generato");
  };

  return (
    <div>
      <PageHeader title="Fornitori" subtitle="Rubrica fornitori, inserimento manuale e da fattura" testid="suppliers-header">
        <Button onClick={generatePDF} variant="outline" className="rounded-full" data-testid="print-suppliers-button">
          <FileDown className="w-4 h-4 mr-1" /> Stampa PDF
        </Button>
        <Button onClick={() => { setEditId(null); setForm({ name: "", phone: "", email: "", address: "", vat: "" }); setNewOpen(true); }} className="rounded-full" data-testid="add-supplier-button">
          <Plus className="w-4 h-4 mr-1" /> Nuovo fornitore
        </Button>
      </PageHeader>
      <Card className="rounded-xl overflow-hidden">
        <Table data-testid="suppliers-table">
          <TableHeader><TableRow><TableHead>Fornitore</TableHead><TableHead className="text-center">Ordini</TableHead><TableHead className="text-right">Totale acquistato</TableHead><TableHead className="text-right">Debito</TableHead><TableHead>Ultima consegna</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Nessun fornitore. Aggiungline uno o si creano automaticamente dalle fatture ricevute.</TableCell></TableRow>}
            {items.map((s) => (
              <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                <TableCell className="font-medium flex items-center gap-2"><Truck className="w-4 h-4 text-primary" />{s.name}</TableCell>
                <TableCell className="text-center">{s.orders || 0}</TableCell>
                <TableCell className="text-right font-medium">{eur(s.total_spent)}</TableCell>
                <TableCell className="text-right">
                  {(s.debt || 0) > 0
                    ? <div className="inline-flex items-center gap-2"><span className="font-medium text-destructive" data-testid={`supplier-debt-${s.id}`}>{eur(s.debt)}</span><Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={() => openPay(s)} data-testid={`supplier-pay-${s.id}`}>Segna come pagata</Button></div>
                    : <span className="text-muted-foreground text-sm">—</span>}
                </TableCell>
                <TableCell>{s.last_date || "—"}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => setSel(s)} data-testid={`supplier-history-${s.id}`}><History className="w-4 h-4 mr-1" /> Storico</Button>
                  <Button size="icon" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-supplier-${s.id}`}><Pencil className="w-4 h-4 text-primary" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(s.id)} data-testid={`delete-supplier-${s.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent data-testid="supplier-history-dialog">
          <DialogHeader><DialogTitle>Storico · {sel?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Consegne</p>
              <div className="space-y-2">
                {(sel?.history || []).slice().reverse().map((h, i) => (
                  <div key={i} className="flex justify-between text-sm border-b border-border/60 pb-2">
                    <span>{h.date} · {h.items} articoli</span>
                    <span className="font-medium">{eur(h.total)}</span>
                  </div>
                ))}
                {(!sel?.history || sel.history.length === 0) && <p className="text-sm text-muted-foreground">Nessuna consegna registrata.</p>}
              </div>
            </div>
            <div data-testid="supplier-payments">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Pagamenti effettuati</p>
              <div className="space-y-2">
                {(sel?.payments || []).slice().reverse().map((pmt, i) => (
                  <div key={i} className="flex justify-between text-sm border-b border-border/60 pb-2" data-testid={`payment-row-${i}`}>
                    <span>{pmt.date} · <span className="text-muted-foreground">{methodLabel(pmt.method)}</span></span>
                    <span className="font-medium text-[hsl(var(--success))]">− {eur(pmt.amount)}</span>
                  </div>
                ))}
                {(!sel?.payments || sel.payments.length === 0) && <p className="text-sm text-muted-foreground">Nessun pagamento registrato.</p>}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={newOpen} onOpenChange={(o) => { setNewOpen(o); if (!o) setEditId(null); }}>
        <DialogContent data-testid="supplier-dialog">
          <DialogHeader><DialogTitle>{editId ? "Modifica fornitore" : "Nuovo fornitore"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Nome / Ragione sociale</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Telefono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="supplier-phone-input" /></div>
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="supplier-email-input" /></div>
            </div>
            <div className="space-y-2"><Label>Indirizzo</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Via, città, CAP" data-testid="supplier-address-input" /></div>
            <div className="space-y-2"><Label>P.IVA / Cod. Fiscale</Label><Input value={form.vat} onChange={(e) => setForm({ ...form, vat: e.target.value })} data-testid="supplier-vat-input" /></div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full" data-testid="save-supplier-button">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="supplier-pay-dialog">
          <DialogHeader><DialogTitle>Segna come pagata · {paySup?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Debito attuale: <span className="font-semibold text-destructive">{eur(paySup?.debt)}</span></p>
            <div className="space-y-2"><Label>Importo da saldare (€)</Label><Input type="number" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="pay-amount-input" /></div>
            <div className="space-y-2"><Label>Metodo di pagamento (esce da)</Label>
              <Select value={payForm.payment_method} onValueChange={(v) => setPayForm({ ...payForm, payment_method: v })}>
                <SelectTrigger data-testid="pay-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>{WALLETS.map((w) => <SelectItem key={w.v} value={w.v}>{w.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={doPay} className="rounded-full" data-testid="confirm-pay-button">Registra pagamento</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
